import { useState, useEffect } from 'react'
import Api from '@vacationtracker/shared/services/api'
import { useIsPostHogFeatureFlagEnabledForCompany } from '@vacationtracker/shared/hooks/use-is-postHog-featureFlag-enabled-for-company'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { RechargeType, ChallengeInfo } from '@vacationtracker/shared/types/recharge-type'

interface UseEnergyScoreProps {
  userId?: string
  currentUserId?: string
  companyId?: string
  requireDevFlag?: boolean // Optional flag to require localStorage dev flag
}

export interface RechargeDetails {
  rechargeType: RechargeType
  completedAt: string
  challengeInfo: ChallengeInfo
}

interface UseEnergyScoreReturn {
  energyScore: number
  isLoading: boolean
  error: string | null
  isFeatureEnabled: boolean
  shouldShowEnergyScore: boolean
  rechargeDetails: RechargeDetails | null
  refetch: () => void
}

/**
 * Custom hook for managing energy score functionality
 * Simple implementation without Redux - perfect for single-component use
 *
 * @param userId - User ID to fetch energy score for (if different from current user)
 * @param currentUserId - Current authenticated user ID
 * @param companyId - Company ID for feature flag checking
 * @param requireDevFlag - Whether to require localStorage dev flag (default: false - not required by default)
 */
export const useEnergyScore = ({
  userId,
  currentUserId,
  companyId,
  requireDevFlag = false, // Default to false - localStorage flag not required by default
}: UseEnergyScoreProps): UseEnergyScoreReturn => {
  const [energyScore, setEnergyScore] = useState<number>(0)
  const [rechargeDetails, setRechargeDetails] = useState<RechargeDetails | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [devEnergyScoreEnabled, setDevEnergyScoreEnabled] = useState(
    () => localStorage.getItem('DEV_ENERGY_SCORE_ENABLED') === 'true'
  )

  // PostHog feature flag check
  const { enabled: isPostHogFeatureEnabled } = useIsPostHogFeatureFlagEnabledForCompany<{ enabled: boolean }>(
    FeatureFlagEnum.userInsightsEnergyScore,
    companyId,
    currentUserId
  )

  // Check PostHog feature flag and conditionally check dev localStorage flag
  const isFeatureEnabled = isPostHogFeatureEnabled && (requireDevFlag ? devEnergyScoreEnabled : true)

  // Determine if we should show the energy score
  const shouldShowEnergyScore = isFeatureEnabled && !isLoading && !error && energyScore > 0

  const fetchEnergyScore = async () => {
    if (!isFeatureEnabled || !currentUserId) return

    try {
      setIsLoading(true)
      setError(null)

      // For now allow only fetching energy level for logged in user
      const isOwnProfile = !userId || userId === currentUserId
      if (!isOwnProfile) {
        return
      }

      const response = await Api.get('/insights/me')

      if (response?.userInsight?.energyScore?.score) {
        setEnergyScore(response.userInsight.energyScore.score as number)
      } else {
        setEnergyScore(0)
      }

      // Extract rechargeDetails from response
      if (response?.rechargeDetails) {
        setRechargeDetails(response.rechargeDetails as RechargeDetails)
      } else {
        setRechargeDetails(null)
      }
    } catch (error) {
      setError('Failed to fetch energy score')
      setEnergyScore(0)
    } finally {
      setIsLoading(false)
    }
  }

  // Fetch energy score when dependencies change
  useEffect(() => {
    if (isFeatureEnabled && currentUserId) {
      fetchEnergyScore()
    }
  }, [isFeatureEnabled, userId, currentUserId, companyId])

  return {
    energyScore,
    isLoading,
    error,
    isFeatureEnabled,
    shouldShowEnergyScore,
    rechargeDetails,
    refetch: fetchEnergyScore,
  }
}
